import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { Search, CalendarCheck, CheckCircle, XCircle } from "react-bootstrap-icons";
import Swal from "sweetalert2";
import axios from "axios";
import API_BASE_URL from "../config";

const AttendanceModal = ({ show, onHide, companyId, companyName }) => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  // Fetch employees when modal opens
  useEffect(() => {
    if (show && companyId) {
      fetchEmployees();
      setCurrentDateTime(new Date());
      setSearchTerm("");
      setSelectedIds(new Set());
    }
  }, [show, companyId]);

  // Filter employees based on search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredEmployees(employees);
    } else {
      const term = searchTerm.toLowerCase().trim();
      const filtered = employees.filter((emp) => {
        const fullName = `${emp.first_name || ""} ${emp.last_name || ""}`.toLowerCase();
        const email = (emp.email || "").toLowerCase();
        const code = (emp.employee_code || emp.id || "").toString().toLowerCase();
        const phone = (emp.phone || "").toLowerCase();
        return (
          fullName.includes(term) ||
          email.includes(term) ||
          code.includes(term) ||
          phone.includes(term)
        );
      });
      setFilteredEmployees(filtered);
    }
  }, [searchTerm, employees]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      // Assumed endpoint - adjust if your backend uses different route
      // Common pattern matching branches: /companies/{id}/employees
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/employees`
      );
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setEmployees(data);
      setFilteredEmployees(data);
    } catch (err) {
      console.error("Failed to fetch employees:", err);
      // Fallback attempt: try alternative common endpoint
      try {
        const res2 = await axios.get(
          `${API_BASE_URL}/employees?company_id=${companyId}`
        );
        const data2 = Array.isArray(res2.data) ? res2.data : res2.data?.data || [];
        setEmployees(data2);
        setFilteredEmployees(data2);
      } catch (err2) {
        console.error("Fallback also failed:", err2);
        Swal.fire({
          icon: "error",
          title: "Failed to load employees",
          text: "Could not fetch employee list for this company.",
        });
        setEmployees([]);
        setFilteredEmployees([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredEmployees.length && filteredEmployees.length > 0) {
      setSelectedIds(new Set());
    } else {
      const allIds = new Set(filteredEmployees.map((emp) => emp.id));
      setSelectedIds(allIds);
    }
  };

  const refreshCurrentTime = () => {
    setCurrentDateTime(new Date());
  };

  // Format for API (YYYY-MM-DD HH:mm:ss)
  const getFormattedDateTime = () => {
    const dateStr = currentDateTime.toISOString().split("T")[0];
    const timeStr = currentDateTime.toISOString().replace("T", " ").slice(0, 19);
    return { dateStr, timeStr };
  };

  const markSelected = async (action) => {
    if (selectedIds.size === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Selection",
        text: "Please select at least one employee.",
      });
      return;
    }

    const { dateStr, timeStr } = getFormattedDateTime();
    const isPresent = action === "present";
    const endpoint = isPresent ? "time_log" : "mark_absent";
    const payload = isPresent
      ? {
          date: dateStr,
          type: "in",
          time: timeStr,
          notes: "",
        }
      : {
          date: dateStr,
          notes: "",
        };

    const actionText = isPresent ? "present" : "absent";
    const confirmColor = isPresent ? "#28a745" : "#dc3545";

    const result = await Swal.fire({
      title: `Mark ${selectedIds.size} employee(s) as ${actionText}?`,
      text: `Date: ${dateStr} | Time (for present): ${currentDateTime.toLocaleTimeString()}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, Mark as ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`,
      cancelButtonText: "Cancel",
      confirmButtonColor: confirmColor,
    });

    if (!result.isConfirmed) return;

    setActionLoading(true);
    const selectedArray = Array.from(selectedIds);

    try {
      const promises = selectedArray.map(async (empId) => {
        try {
          await axios.post(
            `${API_BASE_URL}/employees/${empId}/${endpoint}`,
            payload
          );
          return { empId, success: true };
        } catch (err) {
          console.error(`Failed for employee ${empId}:`, err.response?.data || err.message);
          return { empId, success: false, error: err };
        }
      });

      const results = await Promise.all(promises);
      const successCount = results.filter((r) => r.success).length;
      const failed = results.filter((r) => !r.success);

      if (failed.length === 0) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `${successCount} employee(s) marked as ${actionText} successfully.`,
          timer: 2200,
          showConfirmButton: false,
        });
        setSelectedIds(new Set());
        // Optional: refetch to reflect any status if you add badges later
      } else {
        Swal.fire({
          icon: "warning",
          title: "Partial Success",
          html: `
            <p>${successCount} marked successfully.</p>
            <p class="text-danger">${failed.length} failed. See console for details.</p>
          `,
        });
      }
    } catch (err) {
      console.error("Bulk mark error:", err);
      Swal.fire({
        icon: "error",
        title: "Operation Failed",
        text: "An unexpected error occurred while marking attendance.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const isAllSelected =
    filteredEmployees.length > 0 && selectedIds.size === filteredEmployees.length;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      keyboard={!actionLoading}
    >
      <Modal.Header closeButton className="border-0 pb-2">
        <Modal.Title className="d-flex align-items-center gap-2">
          <CalendarCheck className="text-primary" size={22} />
          <span>Mark Attendance — {companyName}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-2">
        {/* Current Date & Time Banner */}
        <div className="d-flex flex-wrap justify-content-between align-items-center bg-light border rounded-3 p-3 mb-3">
          <div>
            <div className="fw-semibold text-muted small">MARKING FOR</div>
            <div className="fs-5 fw-bold">
              {currentDateTime.toLocaleDateString("en-IN", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
            <div className="text-primary fw-semibold">
              {currentDateTime.toLocaleTimeString("en-IN")}
            </div>
          </div>
          <div className="text-end">
            <Button
              variant="outline-primary"
              size="sm"
              className="rounded-3"
              onClick={refreshCurrentTime}
              disabled={actionLoading}
            >
              Refresh Current Time
            </Button>
            <div className="small text-muted mt-1">
              Time will be used as check-in for "Present"
            </div>
          </div>
        </div>

        {/* Search and Select Controls */}
        <div className="d-flex flex-column flex-md-row gap-2 mb-3 align-items-md-center">
          <InputGroup className="flex-grow-1" style={{ maxWidth: 320 }}>
            <InputGroup.Text className="bg-white">
              <Search size={16} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search by name, email, code or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading || actionLoading}
              className="rounded-end-3"
            />
          </InputGroup>

          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              className="rounded-3 px-3"
              onClick={toggleSelectAll}
              disabled={loading || actionLoading || filteredEmployees.length === 0}
            >
              {isAllSelected ? "Deselect All" : "Select All"}
            </Button>
            <div className="text-muted small align-self-center ms-1">
              {selectedIds.size} / {filteredEmployees.length} selected
            </div>
          </div>
        </div>

        {/* Employees Table */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading employees...</p>
          </div>
        ) : filteredEmployees.length === 0 ? (
          <Alert variant="info" className="rounded-3 text-center">
            {employees.length === 0
              ? "No employees found for this company."
              : "No employees match your search."}
          </Alert>
        ) : (
          <div className="table-responsive border rounded-3" style={{ maxHeight: "420px", overflowY: "auto" }}>
            <Table hover className="mb-0 align-middle">
              <thead className="table-light sticky-top" style={{ zIndex: 1 }}>
                <tr>
                  <th style={{ width: "42px" }} className="text-center">
                    <Form.Check
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                      disabled={actionLoading}
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th style={{ width: "90px" }} className="text-center">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((emp) => {
                  const fullName = `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || "Unnamed Employee";
                  const isSelected = selectedIds.has(emp.id);
                  return (
                    <tr
                      key={emp.id}
                      className={isSelected ? "table-primary" : ""}
                      style={{ cursor: "pointer" }}
                      onClick={() => !actionLoading && toggleSelect(emp.id)}
                    >
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <Form.Check
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(emp.id)}
                          disabled={actionLoading}
                        />
                      </td>
                      <td className="fw-semibold">{fullName}</td>
                      <td className="text-muted small">{emp.email || "—"}</td>
                      <td className="text-muted small">{emp.phone || "—"}</td>
                      <td className="text-center">
                        {isSelected ? (
                          <span className="badge bg-primary rounded-pill px-2 py-1">Selected</span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        <div className="mt-3 small text-muted">
          <strong>Note:</strong> "Present" creates a time log (check-in) for today. "Absent" marks the employee absent for the selected date.
          Existing records will be updated where applicable.
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-2">
        <Button
          variant="outline-secondary"
          className="rounded-3 px-4"
          onClick={onHide}
          disabled={actionLoading}
        >
          Close
        </Button>

        <Button
          variant="success"
          className="rounded-3 px-4 d-flex align-items-center gap-2"
          onClick={() => markSelected("present")}
          disabled={actionLoading || selectedIds.size === 0}
        >
          {actionLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <CheckCircle size={18} />
          )}
          Mark as Present
        </Button>

        <Button
          variant="danger"
          className="rounded-3 px-4 d-flex align-items-center gap-2"
          onClick={() => markSelected("absent")}
          disabled={actionLoading || selectedIds.size === 0}
        >
          {actionLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <XCircle size={18} />
          )}
          Mark as Absent
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AttendanceModal;
